import { Box, Button, Group, Stack, Tooltip, Typography } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { IconEdit } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Markup } from "interweave";
import { useState } from "react";
import type { Recording } from "@/models";
import { useApi } from "@/api";
import { getRecordingDetailsQuery } from "@/api/recordings/getRecordingDetails";
import { updateRecordingSummaryMutation } from "@/api/recordings/updateRecordingSummary";
import { ReviewEditContentModal } from "@/components/ReviewEditContentModal";
import { canAddOrEditRecordings } from "@/security/logic";

export const Route = createFileRoute(
  "/prospects/$prospectId/recordings/$recordingId/summary"
)({
  component: RouteComponent,
  loader: () => {
    return {
      crumb: "Summary"
    };
  }
});

function RouteComponent() {
  const { recordingId } = Route.useParams();
  const { prospect, userClaim } = Route.useRouteContext();
  const { secureAxios } = useApi();
  const queryClient = useQueryClient();

  const { data: recordingDetails } = useQuery(
    getRecordingDetailsQuery(secureAxios, recordingId)
  );

  const hasEditPermission = canAddOrEditRecordings(
    userClaim,
    prospect?.communityId ?? 0
  );

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const updateMutation = useMutation<string, Error, string>({
    ...updateRecordingSummaryMutation(secureAxios, recordingId),
    onSuccess: (updatedSummary: string) => {
      queryClient.setQueryData<Recording | undefined>(
        ["recording", "session", recordingId],
        (old) => (old ? { ...old, summaryHtml: updatedSummary } : old)
      );
      setIsReviewModalOpen(false);
    }
  });

  return (
    <Stack gap="md" h="100%">
      <Box style={{ flex: 1, overflowY: "auto" }}>
        {recordingDetails?.summaryHtml ? (
          <Typography>
            <Markup
              className="markup-content"
              content={recordingDetails.summaryHtml}
            />
          </Typography>
        ) : (
          <div>No summary available.</div>
        )}
      </Box>

      {recordingDetails?.summaryHtml && (
        <Group justify="end">
          <Tooltip
            disabled={hasEditPermission}
            label={
              hasEditPermission
                ? undefined
                : "You do not have access to edit the summary."
            }
          >
            <Button
              disabled={!hasEditPermission}
              leftSection={<IconEdit size={18} />}
              onClick={() => setIsReviewModalOpen(true)}
            >
              Review and Edit Summary
            </Button>
          </Tooltip>
        </Group>
      )}

      {isReviewModalOpen && recordingDetails?.summaryHtml && (
        <ReviewEditContentModal
          title="Recording Summary"
          description="Review and edit the Recording Summary."
          confirmTitle="Update Summary"
          confirmMessage="Are you sure you would like to update the Summary?"
          contentHtml={recordingDetails.summaryHtml}
          isSubmitting={updateMutation.isPending}
          onApprove={(editedHtml) => updateMutation.mutate(editedHtml)}
          onClose={() => setIsReviewModalOpen(false)}
        />
      )}
    </Stack>
  );
}
